"use client";

import React, { useEffect, useRef, useState } from "react";

type Status = "idle" | "sending" | "success" | "error";

interface FormState {
  name: string;
  email: string;
  message: string;
  website: string; // honeypot
  file?: File | null;
}

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mpwlkpar";
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export function ContactForm() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    message: "",
    website: "",
    file: null,
  });
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const [timerDone, setTimerDone] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTimerDone(true), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const valid =
      form.name.trim().length > 1 &&
      /\S+@\S+\.\S+/.test(form.email) &&
      form.message.trim().length > 2;
    setCanSubmit(valid && timerDone && status !== "sending");
  }, [form, timerDone, status]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function clearForm() {
    setForm({ name: "", email: "", message: "", website: "", file: null });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    if (form.website.trim() !== "") {
      clearForm();
      setStatus("success");
      return;
    }

    if (form.file && form.file.size > MAX_FILE_SIZE) {
      setErrorMsg("File too large (max 2MB). Please choose a smaller file.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMsg("");
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("message", form.message);
      fd.append("_subject", "New Portfolio Contact");
      if (form.file) fd.append("attachment", form.file);

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Bad response");
      }
      const json = await res.json();
      if (json.ok || json.success) {
        setStatus("success");
        clearForm();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setStatus("error");
      setErrorMsg("Failed to send. Please try again.");
    }
  }

  const busy = status === "sending";

  return (
    <div className="mx-auto w-full max-w-xl">
      <form
        onSubmit={onSubmit}
        className="space-y-6 rounded-lg border border-edge bg-background/60 p-6 backdrop-blur"
        aria-describedby={errorMsg ? "contact-form-error" : undefined}
        noValidate
      >
        <h2 className="text-2xl font-semibold">Send a Message</h2>
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="cf-name"
          >
            Name <span aria-hidden>*</span>
          </label>
          <input
            id="cf-name"
            name="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            placeholder="Your name"
            className="w-full rounded-md border border-edge bg-muted/40 px-3 py-2 text-sm text-foreground transition outline-none focus:ring-2 focus:ring-primary"
            autoComplete="name"
          />
        </div>
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="cf-email"
          >
            Email <span aria-hidden>*</span>
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-edge bg-muted/40 px-3 py-2 text-sm text-foreground transition outline-none focus:ring-2 focus:ring-primary"
            autoComplete="email"
          />
        </div>
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="cf-message"
          >
            Message <span aria-hidden>*</span>
          </label>
          <textarea
            id="cf-message"
            name="message"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            required
            rows={6}
            placeholder="Write your message..."
            className="w-full resize-y rounded-md border border-edge bg-muted/40 px-3 py-2 text-sm leading-relaxed text-foreground transition outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="cf-file"
          >
            Attachment (optional)
          </label>
          <input
            id="cf-file"
            type="file"
            onChange={(e) => update("file", e.target.files?.[0] || null)}
            className="block w-full text-sm text-foreground file:mr-4 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />
          <p className="text-xs text-muted-foreground">
            Keep file size small (&lt;2MB). Remove if not needed.
          </p>
        </div>
        <div className="hidden">
          <label>
            If you see this, leave it empty
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow transition enabled:hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            aria-busy={busy}
          >
            {busy
              ? "Sending..."
              : status === "success"
                ? "Sent!"
                : "Send Message"}
          </button>
          <span
            className="min-h-[1.25rem] text-sm"
            aria-live="polite"
            id={errorMsg ? "contact-form-error" : undefined}
          >
            {status === "error" && (
              <span className="text-red-400">{errorMsg}</span>
            )}
            {status === "success" && (
              <span className="text-green-400">Message sent successfully.</span>
            )}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Protected by basic spam measures (honeypot & delay). Your info is sent
          via Formspree.
        </p>
      </form>
      <noscript>
        <div className="mt-8 rounded-lg border border-edge bg-background/60 p-4">
          <p className="mb-2 text-sm font-medium">
            JavaScript disabled fallback:
          </p>
          <form action={FORMSPREE_ENDPOINT} method="POST" className="space-y-3">
            <input
              type="hidden"
              name="_subject"
              value="New Portfolio Contact"
            />
            <div>
              <label className="block text-xs font-medium" htmlFor="ns-name">
                Name *
              </label>
              <input
                id="ns-name"
                name="name"
                required
                className="mt-1 w-full rounded border border-edge bg-muted/40 px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium" htmlFor="ns-email">
                Email *
              </label>
              <input
                id="ns-email"
                name="email"
                type="email"
                required
                className="mt-1 w-full rounded border border-edge bg-muted/40 px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium" htmlFor="ns-message">
                Message *
              </label>
              <textarea
                id="ns-message"
                name="message"
                required
                rows={4}
                className="mt-1 w-full rounded border border-edge bg-muted/40 px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium" htmlFor="ns-file">
                Attachment
              </label>
              <input
                id="ns-file"
                name="attachment"
                type="file"
                className="mt-1 w-full text-xs"
              />
            </div>
            <button
              type="submit"
              className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
            >
              Send
            </button>
          </form>
        </div>
      </noscript>
    </div>
  );
}

export default ContactForm;
